import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';

/* 비밀번호 보기/숨기기 눈알 아이콘 */
function EyeIcon({ off }) {
  const common = {
    width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
  };
  return off ? (
    <svg {...common}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg {...common}>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdminMode = searchParams.get('redirect') === 'admin';
  const isDemoLogin = searchParams.get('demoLogin') === '1';
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [welcomeUser, setWelcomeUser] = useState(null);
  const formRef = useRef(null);

  // 포트폴리오 iframe 데모용: ?demoLogin=1 이면 가짜 데모 user 를 localStorage 에 박고 바로 홈 이동
  useEffect(() => {
    if (!isDemoLogin) return;
    try {
      const demoUser = {
        mcode: 'demo',
        name: '데모',
        loginId: 'demo',
        isAdmin: true,
        needPasswordChange: false,
        store_idx: 1,
        team_no: 1,
      };
      localStorage.setItem('plhouse.auth.user', JSON.stringify(demoUser));
    } catch (_) {}
    window.location.replace('/?openOptions=1');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoLogin]);

  useEffect(() => {
    const initialHeight = window.innerHeight;
    const onResize = () => {
      const isKb = window.innerHeight < initialHeight * 0.75;
      setKeyboardOpen(isKb);
    };

    const onFocusIn = () => {
      setTimeout(() => {
        const active = document.activeElement;
        if (active && active.tagName === 'INPUT') {
          active.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    };

    window.addEventListener('resize', onResize);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const id = loginId.trim();
    // 개발/테스트 단축: sangjini582는 비번 없이 입장 (기본 비번 1212 자동 적용)
    const effectivePw = (id === 'sangjini582' && !password.trim()) ? '1212' : password;
    if (!id || !effectivePw.trim()) {
      setError('아이디와 비밀번호를 입력해주세요.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      // 관리자 모드는 스플래시 없이 바로 이동
      if (isAdminMode) {
        const result = await login(id, effectivePw);
        if (!result.success) {
          setError(result.error);
        } else if (!result.user.isAdmin) {
          setError('관리자 권한이 없는 계정입니다.');
        } else {
          window.location.href = '/admin';
        }
        return;
      }

      // 일반 로그인: verifyLogin 성공 → 웰컴 스플래시 → setUser로 홈 이동
      // 로컬(localhost) 또는 ?hold=1 쿼리면 자동 이동 안하고 "홈으로" 버튼 클릭 시 이동
      const holdForever = (typeof window !== 'undefined')
        && (window.location.hostname === 'localhost'
            || window.location.hostname === '127.0.0.1'
            || new URLSearchParams(window.location.search).get('hold') === '1');
      const result = await login(id, effectivePw, {
        onBeforeSet: (user) => new Promise((resolve) => {
          setWelcomeUser({ ...user, _resolve: resolve, _hold: holdForever });
          if (!holdForever) setTimeout(resolve, 1800);
        }),
      });
      if (!result.success) {
        setWelcomeUser(null);
        setError(result.error);
      }
    } catch (err) {
      setError('로그인 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (isAdminMode) {
    return (
      <AdminContainer>
        <AdminLoginCard>
          <AdminLogo>
            <LockIcon>&#128274;</LockIcon>
            <AdminTitle>관리자 페이지</AdminTitle>
            <AdminSub>Admin Login</AdminSub>
          </AdminLogo>

          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <AdminLabel>아이디</AdminLabel>
              <AdminInput
                type="text"
                value={loginId}
                onChange={e => setLoginId(e.target.value)}
                placeholder="관리자 아이디를 입력하세요"
                autoFocus
                autoComplete="username"
              />
            </InputGroup>

            <InputGroup>
              <AdminLabel>비밀번호</AdminLabel>
              <PasswordWrap>
                <AdminInput
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="비밀번호를 입력하세요"
                  autoComplete="current-password"
                  style={{ flex: 1, paddingRight: 44 }}
                />
                <EyeBtn
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  aria-label={showPw ? '비밀번호 숨기기' : '비밀번호 보기'}
                >
                  <EyeIcon off={showPw} />
                </EyeBtn>
              </PasswordWrap>
            </InputGroup>

            {error && <AdminErrorMsg>{error}</AdminErrorMsg>}

            <AdminLoginBtn type="submit" disabled={loading}>
              {loading ? '로그인 중...' : '관리자 로그인'}
            </AdminLoginBtn>
          </Form>

          <AdminFooter>풀잎하우스 관리자 &copy; 2026</AdminFooter>
        </AdminLoginCard>
      </AdminContainer>
    );
  }

  return (
    <Container>
      <LoginInner>
        <Logo $compact={keyboardOpen}>
          <LogoImg $compact={keyboardOpen} src="/logo512.png" alt="풀잎하우스" />
          {!keyboardOpen && <LogoText>풀잎하우스</LogoText>}
          {!keyboardOpen && <LogoSub>매물 관리 시스템</LogoSub>}
        </Logo>

        <Form ref={formRef} onSubmit={handleSubmit}>
          <InputGroup>
            <Input
              type="text"
              value={loginId}
              onChange={e => setLoginId(e.target.value)}
              placeholder="아이디"
              autoFocus
              autoComplete="username"
            />
          </InputGroup>

          <InputGroup>
            <PasswordWrap>
              <Input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="비밀번호"
                autoComplete="current-password"
                style={{ paddingRight: 48 }}
              />
              <EyeBtn
                type="button"
                onClick={() => setShowPw(v => !v)}
                aria-label={showPw ? '비밀번호 숨기기' : '비밀번호 보기'}
              >
                <EyeIcon off={showPw} />
              </EyeBtn>
            </PasswordWrap>
          </InputGroup>

          {error && <ErrorMsg>{error}</ErrorMsg>}

          <LoginBtn type="submit" disabled={loading}>
            {loading ? '로그인 중...' : '로그인'}
          </LoginBtn>
        </Form>

        <Footer>풀잎하우스 &copy; 2026</Footer>
      </LoginInner>

      {welcomeUser && (
        <WelcomeOverlay>
          <WelcomeCard>
            <WelcomeLogo src="/logo512.png" alt="" />
            <WelcomeHello>환영합니다</WelcomeHello>
            <WelcomeLine>
              {welcomeUser.store_name && <WelcomeChip>{welcomeUser.store_name}</WelcomeChip>}
              {welcomeUser.team_no && <WelcomeChip>{welcomeUser.team_no}팀</WelcomeChip>}
            </WelcomeLine>
            <WelcomeName>
              <strong>{welcomeUser.name}</strong>
              {welcomeUser.dept && <span style={{ marginLeft: 6, fontWeight: 600 }}>{welcomeUser.dept}</span>}
              <span style={{ marginLeft: 4 }}>님</span>
            </WelcomeName>
            <WelcomeSub>로그인되었습니다</WelcomeSub>
            {welcomeUser._hold ? (
              <WelcomeContinueBtn onClick={() => { welcomeUser._resolve && welcomeUser._resolve(); }}>
                홈으로 이동
              </WelcomeContinueBtn>
            ) : (
              <WelcomeDots>
                <WelcomeDot $d={0} />
                <WelcomeDot $d={1} />
                <WelcomeDot $d={2} />
              </WelcomeDots>
            )}
          </WelcomeCard>
        </WelcomeOverlay>
      )}
    </Container>
  );
}

/* ── 공통 스타일 ── */
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/* 비밀번호 입력칸 + 눈알 버튼 래퍼 */
const PasswordWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const EyeBtn = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #9CA3AF;
  transition: color 0.15s;
  &:hover { color: #2E6B9E; }
  &:focus { outline: none; }
`;

/* ── 사용자 로그인 스타일 ── */
const Container = styled.div`
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  background: #0A0A0A;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 40px 0;
  @media (min-width: 769px) {
    background: #0A0A0A;
  }
`;

const LoginInner = styled.div`
  width: 100%;
  max-width: 320px;
  padding: 0 24px;
  @media (min-width: 769px) {
    max-width: 400px;
    background: #fff;
    border-radius: 20px;
    padding: 48px 40px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: ${p => p.$compact ? '20px' : '48px'};
  transition: margin-bottom 0.25s ease;
`;

const LogoImg = styled.img`
  width: ${p => p.$compact ? '48px' : '80px'};
  height: ${p => p.$compact ? '48px' : '80px'};
  margin-bottom: ${p => p.$compact ? '0' : '12px'};
  border-radius: 18px;
  filter: drop-shadow(0 4px 12px rgba(0,0,0,0.3));
  transition: all 0.25s ease;
`;

const LogoText = styled.div`
  font-size: 30px;
  font-weight: 700;
  color: #F9FAFB;
  text-shadow: 0 1px 3px rgba(0,0,0,0.4);
  @media (min-width: 769px) {
    color: #111827;
    text-shadow: none;
  }
`;

const LogoSub = styled.div`
  font-size: 13px;
  color: #D1D5DB;
  margin-top: 6px;
  letter-spacing: 1px;
  @media (min-width: 769px) {
    color: #6B7280;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1.5px solid #E5E7EB;
  border-radius: 12px;
  font-size: 16px;
  background: #F9FAFB;
  color: #1F2937;
  transition: border-color 0.2s, background 0.2s;
  &:focus {
    border-color: #2E6B9E;
    background: #fff;
    outline: none;
    box-shadow: 0 0 0 3px rgba(46,107,158,0.1);
  }
  &::placeholder { color: #9CA3AF; }
`;

const ErrorMsg = styled.div`
  color: #EF4444;
  font-size: 13px;
  text-align: center;
  padding: 10px 12px;
  background: #FEF2F2;
  border-radius: 10px;
`;

const LoginBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: #1E3A5F;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  margin-top: 8px;
  transition: all 0.2s;
  &:hover { background: #2E6B9E; }
  &:active { transform: scale(0.98); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 40px;
  font-size: 12px;
  color: #D1D5DB;
  @media (min-width: 769px) {
    color: rgba(255,255,255,0.6);
    margin-top: 24px;
  }
`;

/* ── 로그인 성공 웰컴 스플래시 ── */
const WelcomeOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: #0A0A0A;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: welcomeFade 0.35s ease;
  @keyframes welcomeFade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const WelcomeCard = styled.div`
  text-align: center;
  color: #fff;
  padding: 56px 64px;
  max-width: 92vw;
  animation: welcomePop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  @keyframes welcomePop {
    from { transform: scale(0.8); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
`;

const WelcomeLogo = styled.img`
  width: 140px;
  height: 140px;
  object-fit: contain;
  margin-bottom: 28px;
  filter: drop-shadow(0 6px 18px rgba(0,0,0,0.4));
`;

const WelcomeHello = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: rgba(255,255,255,0.9);
  margin-bottom: 22px;
  letter-spacing: -0.4px;
`;

const WelcomeLine = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const WelcomeChip = styled.span`
  padding: 8px 20px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 999px;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.3px;
`;

const WelcomeName = styled.div`
  font-size: 44px;
  font-weight: 800;
  letter-spacing: -1.2px;
  margin: 14px 0;
  strong { font-size: 50px; }
`;

const WelcomeSub = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: rgba(255,255,255,0.9);
  margin-top: 14px;
  letter-spacing: -0.4px;
`;

const WelcomeDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 36px;
`;

const WelcomeDot = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  opacity: 0.4;
  animation: welcomeBlink 1.2s ease-in-out infinite;
  animation-delay: ${({ $d }) => ($d || 0) * 0.2}s;
  @keyframes welcomeBlink {
    0%, 100% { opacity: 0.3; transform: scale(0.9); }
    50% { opacity: 1; transform: scale(1.1); }
  }
`;

const WelcomeContinueBtn = styled.button`
  margin-top: 36px;
  padding: 16px 44px;
  background: #fff;
  color: #0A0A0A;
  font-size: 18px;
  font-weight: 800;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(255,255,255,0.15);
  transition: transform 0.15s;
  &:hover { transform: scale(1.04); }
  &:active { transform: scale(0.98); }
`;

/* ── 관리자 로그인 스타일 ── */
const AdminContainer = styled.div`
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
`;

const AdminLoginCard = styled.div`
  width: 400px;
  background: #1e1e2e;
  border-radius: 16px;
  padding: 48px 40px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
  border: 1px solid #2a2a3e;
`;

const AdminLogo = styled.div`
  text-align: center;
  margin-bottom: 36px;
`;

const LockIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const AdminTitle = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: #e0e0e0;
`;

const AdminSub = styled.div`
  font-size: 13px;
  color: #888;
  margin-top: 6px;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const AdminLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #aaa;
`;

const AdminInput = styled.input`
  padding: 12px 14px;
  border: 1.5px solid #3a3a4e;
  border-radius: 10px;
  font-size: 15px;
  background: #252538;
  color: #e0e0e0;
  transition: border-color 0.2s;
  &:focus {
    border-color: #5a7bbf;
    box-shadow: 0 0 0 3px rgba(90,123,191,0.2);
  }
  &::placeholder { color: #666; }
`;

const AdminErrorMsg = styled.div`
  color: #ff6b6b;
  font-size: 13px;
  text-align: center;
  padding: 8px;
  background: rgba(255,107,107,0.1);
  border-radius: 8px;
  border: 1px solid rgba(255,107,107,0.2);
`;

const AdminLoginBtn = styled.button`
  padding: 14px;
  background: linear-gradient(135deg, #2a2a4e 0%, #3a3a6e 100%);
  color: #e0e0e0;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
  margin-top: 4px;
  border: 1px solid #4a4a6e;
  transition: all 0.2s;
  &:hover { background: linear-gradient(135deg, #3a3a5e 0%, #4a4a7e 100%); color: #fff; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const AdminFooter = styled.div`
  text-align: center;
  margin-top: 32px;
  font-size: 12px;
  color: #555;
`;
